import {BASE_ACCOUNT_API, fetchAPI} from '@/services/api';
import {RequestPage, ResponsePage} from "@/services/page.interface";

// New backend shape (per request)
export interface CompanyInfo {
    // New backend fields
    companyId?: number;
    companyName?: string;
    companyChineseName?: string;
    /** Multi industry dictionary ids */
    industry?: number[];
    industryName?: string;
    inCorpDate?: string;
    registeredOffice?: string;
    br?: string;
    /** 0=No, 1=Yes — company-level source of truth */
    limitByGuarantee?: number;
    /** true when any YOA FS is COMPLETED */
    limitByGuaranteeLocked?: boolean;
    /** Read-only from GET /company/{id}; strip before create/update */
    registeredOfficeHistories?: CompanyInfoHistory[];
    /** Read-only company English name change history */
    companyNameHistories?: CompanyInfoHistory[];
    /** Read-only company Chinese name change history */
    companyChineseNameHistories?: CompanyInfoHistory[];
}

/** Company field change history (registered_office, company_name, etc.) */
export interface CompanyInfoHistory {
    id?: number;
    companyId?: number;
    yearOfAssessment?: string;
    changeDate?: string;
    columnName?: string;
    /** Historical value (registered office address or former company name) */
    officeAddress?: string;
    /** Alias of officeAddress / history content */
    content?: string;
}

// Legacy alias for backward compatibility
export type Company = CompanyInfo;

export interface RequestCompanyPage extends RequestPage {
    companyName?: string;
    companyChineseName?: string;
    /** Filter: companies whose industry CSV contains this id */
    industry?: number;
    registeredOffice?: string;
    functionalCurrency?: string;
}

/** Normalize API / legacy single id into number[]. */
export function normalizeIndustryIds(value: unknown): number[] {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) {
        return value.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    }
    if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
    }
    const n = Number(value);
    return Number.isFinite(n) ? [n] : [];
}

export interface CompanyPageData {
    records: CompanyInfo[];
    total: number;
    pageNum: number;
    pageSize: number;
}


// Updated base path to /company
const base = BASE_ACCOUNT_API + '/company';

/** Drop read-only history / lock payload before create/update. */
function omitHistories(bo: CompanyInfo): CompanyInfo {
    const {
        registeredOfficeHistories: _officeHistories,
        companyNameHistories: _nameHistories,
        companyChineseNameHistories: _zhHistories,
        limitByGuaranteeLocked: _locked,
        ...rest
    } = bo;
    return rest;
}

export async function getCompany(companyId: number) {
    return fetchAPI<CompanyInfo>(`${base}/${companyId}`);
}

// Accept legacy filters and normalize to { items, total }
export async function pageCompanies(payload?: RequestCompanyPage) {
    return fetchAPI<ResponsePage<CompanyInfo>>(`${base}/queryPage`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function querySelectionList() {
    return fetchAPI<[CompanyInfo]>(`${base}/querySelectionList`, {
        method: 'GET',
    });
}

export async function createCompany(bo: CompanyInfo) {
    return fetchAPI<CompanyInfo>(`${base}/create`, {
        method: 'POST',
        body: JSON.stringify(omitHistories(bo)),
    });
}

export async function updateCompany(companyId: number, bo: CompanyInfo) {
    return fetchAPI<CompanyInfo>(`${base}/update/${companyId}`, {
        method: 'POST',
        body: JSON.stringify(omitHistories(bo)),
    });
}

export async function deleteCompany(companyId: number) {
    return fetchAPI<void>(`${base}/delete/${companyId}`, {method: 'POST'});
}

/** CR GOV 代理查询整理后的单条记录（不落库） */
export interface CrGovCompanyHit {
    companyName?: string;
    companyChineseName?: string;
    /** dd/MM/yyyy */
    incorporationDate?: string;
    brNo?: string;
    source?: string;
}

/**
 * 经后端代理查询香港 CR（local→foreign），仅返回整理数据，不保存。
 * @param companyName 公司名（后端再转全大写）
 */
export async function searchCrGovByCompanyName(companyName: string) {
    const q = encodeURIComponent(companyName);
    return fetchAPI<CrGovCompanyHit[]>(`${base}/cr-gov/search?companyName=${q}`, {
        method: 'GET',
    });
}
